import { IDisposable } from "../../../Global/Base/IDisposable";
import { Deferred, PromiseUtils } from "../../../Global/Base/Promises/PromiseUtils";
import { Key, SharedKey } from "../../../Global/Base/Utils/EventUtils";
import { StringUtils } from "../../../Global/Base/Utils/StringUtils";
import { Localization } from "../../../OW.Remoting/Remoting/Localization";
import { EventHelper } from "../../UI.BaseControls/Helpers/EventHelper";
import { createElement } from "../../UI.ElementarControls/Utils/CustomElementUtils";
import { EditableControl } from "./Controls/EditableControl";
import { IContentBuilder } from "./DataBuilders/IContentBuilder";
import { MessageButtonResult, UIManager } from "./UIManager";
/**
 * Modální okno umožňující zobrazovat libovolné informace.
 * Je možné ho zobrazit i jako potvrzovací nebo dotazovací dialog.
 * @includeToDoc
 */
export class MessageWindow implements IDisposable {
    /**
     * Kontejner zobrazující obsah
     */
    public get element(): HTMLElement {
        return this._element;
    }
    private _element: HTMLElement;

    /**
     * Šířka okna pro zobrazení hlášky
     */
    public width: number;

    /**
     * Výška okna pro zobrazení zprávy
     */
    public height: number;

    /**
     * Lokalizační klíč tlačítka pro zrušení.
     * Tlačítko se zobrazí jen tehdy, pokud se bude jednat o hlášku (dialog).
     * V případě alertu se nezobrazí.
     */
    public cancelButtonCaption: string = "";
    /**
     * Výběr použití zprávy:
     * · zpráva na úrovni aplikace
     * · zpráva na úrovni formuláře
     */
    public context: MessageContext = MessageContext.form;

    /**
     * Do této části se bude vkládat případný detail zprávy. Pokud je vyplněn zobrazí se DetailButton.
     */
    public detail: string = "";

    /**
     * Tlačítko pro zobrazení detailu.
     */
    public detailButton: boolean = false;
    /**
     * Zda se má zobrazit s otevřeným detailem, pokud existuje
     */
    public detailOpened: boolean = false;
    /**
     * Lokalizační klíč nadpisu dialogu
     */
    public header: string = "";
    /**
     * Lokalizační klíč tlačítka pro potvrzení nebo zavření alertu. Tlačítko se zobrazí vždy
     */
    public okButtonCaption: string = "GUI-0";
    /**
     * Lokalizační klíč tlačítka pro NE při YesNoCancelDialogu
     */
    public noButtonCaption: string = "";
    /**
     * Lokalizační klíč tlačítka pro definovanou operaci.
     * Tlačítko se nezobrazí, pokud nebude definovaná operace.
     * Tlačítko se zobrazí jen tehdy, pokud se bude jednat o hlášku (dialog).
     * V případě alertu se nezobrazí
     */
    public otherButtonCaption: string = "";
    /**
     * Lokalizační klíč tlačítka 1 v leve casti dialogu
     */
    public leftButton1Caption: string = "";
    /**
     * Lokalizační klíč tlačítka 2 v leve casti dialogu
     */
    public leftButton2Caption: string = "";
    /**
     * Hlavní text zprávy
     */
    public contentBuilder: IContentBuilder;
    /**
     * Typ zprávy
     */
    public type: MessageType;
    /**
     * Umožňuje zablokovat propagování události kliknutí do okna
     */
    public stopClickPropagation: boolean = false;
    private _deferredResult: Deferred<MessageButtonResult>;

    private readonly _minHeightWithDetail: string = "218px";
    private readonly _minHeightWithoutDetail: string = "134px";
    private _buttonHasClicked: boolean = false;
    private _messageId: string;
    private _headerElement: HTMLElement;
    private _contentElement: HTMLElement;
    private _detailElement: HTMLElement;
    private _detailContentElement: HTMLElement;
    private _buttonsElement: HTMLElement;
    private _leftButtonsElement: HTMLElement;
    private _standardButtonsElement: HTMLElement;

    private _okButtonElement: HTMLButtonElement;
    private _noButtonElement: HTMLButtonElement;
    private _cancelButtonElement: HTMLButtonElement;
    private _otherButtonElement: HTMLButtonElement;
    private _leftButton1Element: HTMLButtonElement;
    private _leftButton2Element: HTMLButtonElement;
    private _blockingLayer: HTMLElement;
    private _showDetailButtonElement: HTMLElement;
    private _focusElement: HTMLElement | EditableControl;
    private _resolver: (result: MessageButtonType) => void;

    private _buttonsList: HTMLButtonElement[] = [];
    private _focusedButtonIndex: number = 0;

    private _eventListenerMethods: { [listenerName: string]: (e: Event) => any } = {};
    private static _countDisplayMessage: number = 0;

    public static get countDisplayMessage(): number {
        return this._countDisplayMessage;
    }

    private _lastDetailHeight: string = "";

    /** Sekce pro zobrazování popup dialogů. */
    private _serviceSection: HTMLDivElement;

    /** Původní element, který byl zaměřený, než se zobrazilo MessageWindow. */
    private _originalActiveElement: Element;

    /** Callback, který se zavolá při stisknutí OK tlačítka. Pokud vrátí false, dialog zůstane otevřený. */
    public okCallback: () => Promise<boolean> = undefined;

    public otherCallback: () => Promise<boolean> = undefined;

    public left1Callback: () => Promise<boolean> = undefined;

    public left2Callback: () => Promise<boolean> = undefined;

    public get okButton(): HTMLButtonElement {
        return this._okButtonElement;
    }

    public get noButton(): HTMLButtonElement {
        return this._noButtonElement;
    }

    public get cancelButton(): HTMLButtonElement {
        return this._cancelButtonElement;
    }

    public get otherButton(): HTMLButtonElement {
        return this._otherButtonElement;
    }

    public get leftButton1(): HTMLButtonElement {
        return this._leftButton1Element;
    }

    public get leftButton2(): HTMLButtonElement {
        return this._leftButton2Element;
    }

    /**
     * Určuje element, který má dostat po otevření okna focus
     */
    public get focusElement(): HTMLElement | EditableControl {
        return this._focusElement;
    }

    public set focusElement(element: HTMLElement | EditableControl) {
        this._focusElement = element;
    }

    /**
     * Schová MessageBox za blokovací vrstvu
     */
    public get blocked(): boolean {
        return this._element.classList.contains("blocked");
    }

    public set blocked(value: boolean) {
        if (value)
            this._showAndIncBlockingLayer();
        else
            this._hideOrDecBlockingLayer();
        this._element.classList.toggle("blocked", value);
    }
    private _logsUserInteraction: boolean = true;

    /** Vrací příznak, jestli dialog loguje akce jako uživatelské. Uživatelské akce prodlužují session pokud je tato vlastnost zapnutá.
     * Výchozí hodnota: true
     */
    public get logsUserInteraction(): boolean {
        return this._logsUserInteraction;
    }
    /** Nastavuje příznak, jestli dialog loguje akce jako uživatelské. Uživatelské akce prodlužují session pokud je tato vlastnost zapnutá.
     * Výchozí hodnota: true
     */
    public set logsUserInteraction(val: boolean) {
        this._logsUserInteraction = val;
        if (this._element)
            this._element.classList.toggle("no-interaction-container", !val);
    }

    /**
     * Vytvoří dialogové okno
     * @param contentBuilder Builder obsahu okna.
     * @param _additionalClasses Dodatečné css třídy, které budou přidány do dialogu.
     */
    constructor(contentBuilder?: IContentBuilder, private readonly _additionalClasses?: string[]) {
        this.contentBuilder = contentBuilder;
        this._blockingLayer = UIManager.instance.blockingLayer;
        this.type = MessageType.message;
    }

    /**
     * Zobrazí blokující vrstvu a inkrementuje počítadlo oken
     */
    private _showAndIncBlockingLayer(): void {
        UIManager.instance.showBlockingLayer();
        if (MessageWindow._countDisplayMessage === 0) {
            if (this._blockingLayer)
                this._blockingLayer.classList.add("animation");
        }
        MessageWindow._countDisplayMessage++;
    }

    /**
     * Dekrementuje počítadlo oken a v případě posledního skryje blokující vrstvu
     */
    private _hideOrDecBlockingLayer(): void {
        MessageWindow._countDisplayMessage--;
        if (MessageWindow._countDisplayMessage <= 0) {
            MessageWindow._countDisplayMessage = 0;
            if (this._blockingLayer)
                this._blockingLayer.classList.add("animation", "animation-backwards");
            document.body.style.overflow = "";
            UIManager.instance.hideBlockingLayer();
        }
    }

    /**
     * Zobrazí okno na základě zadaných parametrů
     */
    public async show(): Promise<MessageButtonResult> {
        if (!this._messageId) {
            this._deferredResult = PromiseUtils.defer<MessageButtonResult>();

            await this._createElement();
            this._showAndIncBlockingLayer();
            this._serviceSection = document.createElement("div");
            this._serviceSection.classList.add("service-section");
            document.body.appendChild(this._serviceSection);
            this._serviceSection.appendChild(this._element);
            document.body.style.overflow = "hidden";

            this._element.classList.add("animation");

            if (this.detailButton && this.detailOpened)
                this._toggleDetailOpenedState();
        }

        this._originalActiveElement = document.activeElement;

        if (this.focusElement) { // Pokud je nastavena tato property, má prioritu, pro element k označení
            this.focusElement.focus();
        } else {
            // Pri show message window nastavuje focus na tlacitko OK, pokud neni tlacitko tak focus dostane cely okno
            if (this.okButtonCaption && this._okButtonElement) {
                this._okButtonElement.focus();
            } else {
                this._element.focus();
            }
        }

        return this._deferredResult.promise;
    }

    /**
     * Metoda, která zobrazá okno přímo jako alert, bez nutnosti konfigurovat třídu.
     * @param data Lokalizační klíč textu
     * @param titleLocKey [Nepovinný] Titulek okna pokud není zadán nebude zobrazen
     */
    public static showAlert(data: IContentBuilder, titleLocKey?: string): Promise<MessageButtonResult> {
        const m = new MessageWindow(data);
        m.type = MessageType.alert;
        if (titleLocKey)
            m.header = titleLocKey;
        return m.show();
    }

    /**
     * Zobrazeni potvrzovaciho dialogu ANO / NE
     * @param data text dialogu
     * @param OKButtonLC lokalizační klíč pro OKButton, GUI-146838=Ano
     * @param NoButtonLC lokalizační klíč pro NoButton, GUI-146840=Ne
     * @param titleLocKey [Nepovinný] Titulek okna pokud není zadán nebude zobrazen
     */
    public static showYesNoDialog(data: IContentBuilder, OKButtonLC: string = "GUI-146838", NoButtonLC: string = "GUI-146840", titleLocKey?: string): Promise<MessageButtonResult> {
        const m = new MessageWindow(data);
        m.okButtonCaption = OKButtonLC;
        m.noButtonCaption = NoButtonLC;
        m.type = MessageType.message;
        if (titleLocKey)
            m.header = titleLocKey;
        return m.show();
    }

    /**
     * Zobrazeni potvrzovaciho dialogu ANO / NE / Cancel
     * @param data text dialogu
     * @param OKButtonLC lokalizační klíč pro OKButton, GUI-146838=Ano
     * @param NoButtonLC lokalizační klíč pro NoButton, GUI-146840=Ne
     * @param CancelButtonLC lokalizační klíč pro CancelButton, GUI-33682=Storno
     */
    public static showYesNoCancelDialog(data: IContentBuilder, OKButtonLC: string = "GUI-146838", NoButtonLC: string = "GUI-146840", CancelButtonLC: string = "GUI-33682"): Promise<MessageButtonResult> {
        const m = new MessageWindow(data);
        m.okButtonCaption = OKButtonLC;
        m.noButtonCaption = NoButtonLC;
        m.cancelButtonCaption = CancelButtonLC;
        m.type = MessageType.message;
        return m.show();
    }

    /**
     * Zobrazí potvrzovací dialog
     * @param data Text dialogu
     * @param titleLocKey Titulek dialogu
     */
    public static showConfirm(data: IContentBuilder, titleLocKey?: string): Promise<MessageButtonResult> {
        const m = new MessageWindow(data);
        m.type = MessageType.message;
        m.cancelButtonCaption = "GUI-2";
        if (titleLocKey)
            m.header = titleLocKey;
        return m.show();
    }

    /**
     * Pokud je zobrazena debugovací hláška používá se v textu hlášky přímo psany text, nikoli lokalizační klíč
     * @param data Text, který bud zobrazen v hlášce
     */
    public static showDebug(data: IContentBuilder): Promise<MessageButtonResult> {
        const m = new MessageWindow(data);
        m.type = MessageType.debug;
        return m.show();
    }

    public close(buttonType: MessageButtonType): void {
        this._deferredResult.resolve({
            result: buttonType,
        });
        if (this._resolver) {
            this._resolver(buttonType);
        }
        if (this._originalActiveElement instanceof HTMLElement) {
            // Vrátíme zaměření na původní element.
            this._originalActiveElement.focus();
        }
    }

    private _updateFocusedButtonIndex(key: SharedKey): void {
        if (key === Key.ArrowLeft) {
            this._focusedButtonIndex = (this._focusedButtonIndex + this._buttonsList.length - 1) % this._buttonsList.length;
        } else {
            this._focusedButtonIndex = (this._focusedButtonIndex + 1) % this._buttonsList.length;
        }
    }

    private async _createElement(): Promise<void> {
        this._messageId = UIManager.instance.generateElementID();

        this._element = createElement({ attrs: { "id": this._messageId }, classes: "MessageBox" });
        this._element.tabIndex = 0; //potřeba kvůli tomu, aby případný keyup nad overlayem měl v eventě target element, jinak by tam byl document
        this.logsUserInteraction = this._logsUserInteraction; //nastaví class pro logování uživatelské interakce

        if (this._additionalClasses) {
            this._additionalClasses.forEach(i => this._element.classList.add(i));
        }

        if (this.header && this.type !== MessageType.information) {
            this._headerElement = createElement({ classes: "Header" });
            Localization.addLocTxtData(this._headerElement, this.header);
            this._element.appendChild(this._headerElement);
        }

        this._contentElement = createElement({ classes: "Content" });
        if (this.contentBuilder) {
            this._contentElement.innerHTML = "";
        }
        this._element.appendChild(this._contentElement);

        if (this.type !== MessageType.empty) {
            this._buttonsList = [];

            this._buttonsElement = createElement({ classes: "bottom-buttons" });
            this._standardButtonsElement = createElement({ classes: "standard-buttons" });

            if (this.type === MessageType.message) {
                this._leftButtonsElement = createElement({ classes: "left-buttons" });

                if (this.otherButtonCaption) {
                    this._otherButtonElement = this._createButton(this.otherButtonCaption, "OtherButton", "flat");
                    this._standardButtonsElement.appendChild(this._otherButtonElement);
                    this._buttonsList.push(this._otherButtonElement);
                }
                if (this.cancelButtonCaption) {
                    this._cancelButtonElement = this._createButton(this.cancelButtonCaption, "cancel", "flat");
                    this._standardButtonsElement.appendChild(this._cancelButtonElement);
                    this._buttonsList.push(this._cancelButtonElement);
                }
                if (this.noButtonCaption) {
                    this._noButtonElement = this._createButton(this.noButtonCaption, "NoButton", "flat");
                    this._standardButtonsElement.appendChild(this._noButtonElement);
                    this._buttonsList.push(this._noButtonElement);
                }
                if (this.leftButton1Caption) {
                    this._leftButton1Element = this._createButton(this.leftButton1Caption, "OtherButton", "flat");
                    this._leftButtonsElement.appendChild(this._leftButton1Element);
                    this._buttonsList.push(this._leftButton1Element);
                }
                if (this.leftButton2Caption) {
                    this._leftButton2Element = this._createButton(this.leftButton2Caption, "OtherButton", "flat");
                    this._leftButtonsElement.appendChild(this._leftButton2Element);
                    this._buttonsList.push(this._leftButton2Element);
                }

                this._buttonsElement.appendChild(this._leftButtonsElement);
            }

            this._okButtonElement = this._createButton(this.okButtonCaption, "OKButton", "primary");
            this._standardButtonsElement.appendChild(this._okButtonElement);
            this._buttonsList.push(this._okButtonElement);

            this._buttonsElement.appendChild(this._standardButtonsElement);

            this._element.appendChild(this._buttonsElement);
        }

        if (this.detailButton) {
            this._createDetailButton();
            this._element.style.minHeight = this._minHeightWithDetail;

            if ($)
                $(this._element)?.resizable({ handles: "n, e, s, w, ne, se, sw, nw" });

            this._element.appendChild(this._detailElement);
        } else {
            this._element.style.minHeight = this._minHeightWithoutDetail;
        }
        if (this.contentBuilder)
            this._contentElement.appendChild(await this.contentBuilder.build());

        this._setListeners();
    }

    private _createButton(locKey: string, ...classes: string[]): HTMLButtonElement {
        const button = document.createElement("button");
        button.classList.add(...classes, "btn", "btn-large");
        Localization.addLocTxtData(button, locKey);
        return button;
    }

    private _createDetailButton(): void {
        this._detailElement = createElement({ classes: ["DetailInformation", "hidden"] });
        this._showDetailButtonElement = createElement({ classes: ["ShowDetail"] });
        Localization.addLocTxtData(this._showDetailButtonElement, "GUI-331736"); // Zobrazit detail

        const cc = createElement({ classes: ["cc"] });
        this._detailContentElement = createElement({ classes: ["Content"] });
        if (this.detail) {
            this.detail = StringUtils.stringToHTMLText(this.detail);
            this._detailContentElement.innerHTML = this.detail;
        }
        cc.appendChild(this._detailContentElement);
        this._detailElement.appendChild(this._showDetailButtonElement);
        this._detailElement.appendChild(cc);
    }

    private _setListeners(): void {
        // pridani eventu na ukonceni animace
        // ReSharper disable once Html.EventNotResolved
        this._element.addEventListener("animationend", this._eventListenerMethods["animationend"] = () => {
            this._element.classList.remove("animation");
            if (this._blockingLayer) {
                this._blockingLayer.classList.remove("animation", "animation-backwards");
            }
            if (this._element.classList.contains("animation-backwards")) {
                this.dispose();
                // odebrani z domu
                this._element.remove();
            }
        });

        if (this.stopClickPropagation)
            this._element.addEventListener("click", this._eventListenerMethods["clickStopPropagation"] = e => e.stopPropagation());

        /** Pomocná funkce pro zaměření elementu (pokud existuje) a zastavení propagace eventu. */
        const focusHelper = (element: HTMLElement | undefined, e: Event) => {
            if (element) {
                element.focus();
                e.preventDefault();
            }
            return element;
        };

        this._element.addEventListener("keydown", this._eventListenerMethods["keydown"] = async (e: KeyboardEvent) => {
            if (e.key === Key.Escape) {
                if (this.cancelButtonCaption) {
                    resultMethod(MessageButtonType.cancel);
                    return;
                } else if (this.noButtonCaption) {
                    resultMethod(MessageButtonType.no);
                    return;
                }
            }

            /** Focus trap pro udržení focusu v MessageWindow elementu. */
            if (e.key === Key.Tab) {
                const inputs = [...this.element.getElementsByTagName("input")];
                const firstInput = inputs[0];
                const lastInput = inputs.slice(-1)[0];
                const buttons = [...this._buttonsElement.getElementsByTagName("button")];
                const firstButton = buttons[0];
                const lastButton = buttons.slice(-1)[0];
                if (e.shiftKey) {
                    if (e.target === firstInput) {
                        focusHelper(lastButton, e) || focusHelper(lastInput, e);
                    } else if (e.target === firstButton) {
                        if (!lastInput) {
                            focusHelper(lastButton, e);
                        }
                    }
                } else {
                    if (e.target === lastButton) {
                        focusHelper(firstInput, e) || focusHelper(firstButton, e);
                    } else if (e.target === lastInput) {
                        if (!firstButton) {
                            focusHelper(firstInput, e);
                        }
                    }
                }
            }
        });

        /** Přepínání spodních tlačítek šipkami */
        this._buttonsElement?.addEventListener("keydown", this._eventListenerMethods["keydownButtons"] = async (e: KeyboardEvent) => {
            if (e.key === Key.ArrowLeft || e.key === Key.ArrowRight) {
                this._updateFocusedButtonIndex(e.key);
                this._buttonsList[this._focusedButtonIndex].focus();
            }
        });

        for (const button of this._buttonsList) {
            button.addEventListener("focus", () => {
                this._focusedButtonIndex = this._buttonsList.indexOf(button);
            });
        }

        const resultMethod = (buttonType: MessageButtonType) => {
            if (this._buttonHasClicked)
                return;

            //this._element.remove();
            this._element.classList.add("animation", "animation-backwards");

            document.body.removeChild(this._serviceSection);

            this._hideOrDecBlockingLayer();

            this._buttonHasClicked = true;

            this.close(buttonType);
        };

        this._resolver = (result: MessageButtonType) => resultMethod(result);

        if (this._okButtonElement)
            this._okButtonElement.addEventListener("click", this._eventListenerMethods["OKButtonClick"] = async (e: MouseEvent) => {
                if (this._okButtonElement.classList.contains("Disabled"))
                    return;
                EventHelper.stopPropagation(e);
                if (this.okCallback) {
                    try {
                        this._okButtonElement.setAttribute("disabled", "disabled");
                        this._okButtonElement.classList.add("Disabled");
                        this._okButtonElement.classList.add("loading");
                        if (await this.okCallback()) //pokud callback vrátil false, dialog zůstane otevřený
                            resultMethod(MessageButtonType.ok);
                    } finally {
                        this._okButtonElement.classList.remove("loading");
                        this._okButtonElement.removeAttribute("disabled");
                        this._okButtonElement.classList.remove("Disabled");
                    }
                } else {
                    resultMethod(MessageButtonType.ok);
                }
            });
        if (this._noButtonElement)
            this._noButtonElement.addEventListener("click", this._eventListenerMethods["NOButtonClick"] = (e: MouseEvent) => {
                EventHelper.stopPropagation(e);
                resultMethod(MessageButtonType.no);
            });
        if (this._cancelButtonElement)
            this._cancelButtonElement.addEventListener("click", this._eventListenerMethods["CancelButtonClick"] = (e: MouseEvent) => {
                EventHelper.stopPropagation(e);
                resultMethod(MessageButtonType.cancel);
            });
        if (this._otherButtonElement)
            this._otherButtonElement.addEventListener("click", this._eventListenerMethods["OtherButtonClick"] = async (e: MouseEvent) => {
                EventHelper.stopPropagation(e);
                if (!this.otherCallback || (this.otherCallback && await this.otherCallback())) { // pokud callback vrátil false, dialog zůstane otevřený
                    resultMethod(MessageButtonType.other);
                }
            });
        if (this._leftButton1Element) {
            this._leftButton1Element.addEventListener("click", this._eventListenerMethods["LeftButton1Click"] = async (e: MouseEvent) => {
                EventHelper.stopPropagation(e);
                if (!this.left1Callback || (this.left1Callback && await this.left1Callback())) { // pokud callback vrátil false, dialog zůstane otevřený
                    resultMethod(MessageButtonType.leftButton1);
                }
            });
        }
        if (this._leftButton2Element) {
            this._leftButton2Element.addEventListener("click", this._eventListenerMethods["LeftButton2Click"] = async (e: MouseEvent) => {
                EventHelper.stopPropagation(e);
                if (!this.left2Callback || (this.left2Callback && await this.left2Callback())) { // pokud callback vrátil false, dialog zůstane otevřený
                    resultMethod(MessageButtonType.leftButton2);
                }
            });
        }
        if (this._showDetailButtonElement) {
            this._showDetailButtonElement.addEventListener("click", this._eventListenerMethods["showDetailButtonClick"] = () => this._toggleDetailOpenedState());
        }
    }

    private _toggleDetailOpenedState(): void {
        this._detailElement.classList.toggle("hidden");

        if (this._detailElement.classList.contains("hidden")) {
            this._element.style.minHeight = this._minHeightWithDetail;
            this._lastDetailHeight = this._element.style.height;
            this._element.style.height = "";
            Localization.addLocTxtData(this._showDetailButtonElement, "GUI-331736"); // Zobrazit detail
        } else {
            this._element.style.minHeight = (this._headerElement.clientHeight + this._contentElement.clientHeight + this._buttonsElement.clientHeight + this._showDetailButtonElement.clientHeight + 150) + "px";
            this._element.style.height = this._lastDetailHeight === "" ? (this._detailContentElement.clientHeight + parseInt(this._element.style.minHeight)) + "px" : this._lastDetailHeight;
            Localization.addLocTxtData(this._showDetailButtonElement, "GUI-176350"); // Skrýt detail
        }
    }

    //#region IDisposable
    public dispose(): void {
        this._buttonHasClicked = false;
        if (this._element) {
            this._element.removeEventListener("click", this._eventListenerMethods["clickStopPropagation"]);
            this._element.removeEventListener("animationend", this._eventListenerMethods["animationend"]);
            this._element.removeEventListener("keydown", this._eventListenerMethods["keydown"]);
        }
        if (this._buttonsElement) {
            this._buttonsElement.removeEventListener("keydown", this._eventListenerMethods["keydownButtons"]);
        }
        if (this._okButtonElement)
            this._okButtonElement.removeEventListener("click", this._eventListenerMethods["OKButtonClick"]);
        if (this._noButtonElement)
            this._noButtonElement.removeEventListener("click", this._eventListenerMethods["NOButtonClick"]);
        if (this._cancelButtonElement)
            this._cancelButtonElement.removeEventListener("click", this._eventListenerMethods["CancelButtonClick"]);
        if (this._otherButtonElement)
            this._otherButtonElement.removeEventListener("click", this._eventListenerMethods["OtherButtonClick"]);
        if (this._leftButton1Element)
            this._leftButton1Element.removeEventListener("click", this._eventListenerMethods["LeftButton1Click"]);
        if (this._leftButton2Element)
            this._leftButton2Element.removeEventListener("click", this._eventListenerMethods["LeftButton2Click"]);
        if (this._showDetailButtonElement)
            this._showDetailButtonElement.removeEventListener("click", this._eventListenerMethods["showDetailButtonClick"]);
    }

    //#endregion IDisposable
}

/**
 * Typ hlášky
 */
export enum MessageType {
    error,
    alert,
    information,
    message,
    debug,
    empty,
}

export enum MessageContext {
    application,
    form,
}

/**
 * Typ stisknutého tlacitka pro navratovy promise
 */
export enum MessageButtonType {
    ok,
    cancel,
    no,
    other,
    leftButton1,
    leftButton2,
}
